import { QuickActionOrigin, QuickActions } from "@faire/help-center-utils/components/QuickActions/QuickActions";
import { getRetailerOrBrandToken } from "@faire/help-center-utils/serialized-data/getRetailerOrBrandToken";
import { hasShortcutsAccess } from "@faire/help-center-utils/serialized-data/shortcutsAccess";
import { trackCxFoundationsL1IssueClick } from "@faire/web-api/events/cxFoundations/click/l1Issue";
import { trackCxFoundationsTopicSelectPageView } from "@faire/web-api/events/cxFoundations/view/topicSelectPage";
import { QueryParameters as supportSelfServeParams } from "@faire/web-api/routes/support/self-serve";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect } from "react";
import styled from "styled-components";
import { IssueTopics } from "../../components/IssueTopics";
import { useClearNodesInLocalStorageOnLoad } from "../../hooks/useClearNodesInLocalStorageOnLoad";
import { useNavigation } from "../../hooks/useNavigation";
import { useNodeSetter } from "../../hooks/useNodeSetter";
import { useSelectedNode } from "../../hooks/useSelectedNode";
import { useSkipNodeSelection } from "../../hooks/useSkipNodeSelection";
import { useTrackPageViewEffect } from "../../hooks/useTrackPageViewEffect";
import { useTree } from "../../hooks/useTree";
var PAGE_JS_EXECUTED_MARK = markPageJsExecuted();
export var HomeWithShortcuts = function HomeWithShortcuts(_ref) {
  var isLoading = _ref.isLoading,
    dataUpdatedAt = _ref.dataUpdatedAt;
  useTrackPageViewEffect(trackCxFoundationsTopicSelectPageView);
  useClearNodesInLocalStorageOnLoad();
  var _useViewport = useViewport(),
    isMobile = _useViewport.isMobile;
  var _useTree = useTree(),
    tree = _useTree.tree;
  var _useSelectedNode = useSelectedNode(),
    level1Node = _useSelectedNode.level1Node;
  var _useNodeSetter = useNodeSetter(),
    setLevel1Node = _useNodeSetter.setLevel1Node;
  var _useNavigation = useNavigation(),
    goToNextStep = _useNavigation.goToNextStep;
  var goToNextStepRef = React.useRef(goToNextStep);
  goToNextStepRef.current = goToNextStep;
  var retailerOrBrandToken = getRetailerOrBrandToken();
  var loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "help_center-page_self_serve_home",
    enableTrackPerformanceBreakdown: "internal-tracking",
    isPageLoading: isLoading,
    pageLoadKey: "help_center-page_self_serve_home",
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    dataUpdatedAt: dataUpdatedAt
  });
  var _useQueryParams = useQueryParams(supportSelfServeParams.parse),
    brandOrderToken = _useQueryParams.brand_order_token;
  var _useSkipNodeSelection = useSkipNodeSelection(),
    skipOrderSelection = _useSkipNodeSelection.skipOrderSelection,
    nodeSelected = _useSkipNodeSelection.nodeSelected,
    setNodeSelected = _useSkipNodeSelection.setNodeSelected;
  useEffect(function () {
    if (brandOrderToken) {
      setNodeSelected(true);
    }
  }, [brandOrderToken, setNodeSelected]);
  useEffect(function () {
    if (nodeSelected && level1Node) {
      goToNextStepRef.current({
        skipOrderSelection: skipOrderSelection
      });
      setNodeSelected(false);
    }
  }, [nodeSelected, level1Node, skipOrderSelection, setNodeSelected]);
  var handleNodeSelection = function handleNodeSelection(node) {
    setLevel1Node(node);
    setNodeSelected(true);
    trackCxFoundationsL1IssueClick(node.id, retailerOrBrandToken);
  };
  return /*#__PURE__*/React.createElement(GridWrapper, {
    "data-test-id": "self-serve-home-wrapper"
  }, /*#__PURE__*/React.createElement(IssueTopicsWrapper, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "sectionHeaderSansMedium"
  }, /*#__PURE__*/React.createElement(StrictLocalMsg, {
    id: "x+eH0H",
    defaultMessage: [{
      "type": 0,
      "value": "What can we help with?"
    }]
  })), /*#__PURE__*/React.createElement(Spacer, {
    height: "3x"
  }), /*#__PURE__*/React.createElement(IssueTopics, {
    parentNode: tree["Root"],
    selectedNode: level1Node,
    setSelectedNode: handleNodeSelection,
    loadTimingRef: loadTimingRef
  })), isMobile || !hasShortcutsAccess() ? null : ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GridSpacer, null), /*#__PURE__*/React.createElement(ShortcutsWrapper, null, /*#__PURE__*/React.createElement(QuickActions, {
    origin: QuickActionOrigin.SelfServe
  })))));
};
var GridWrapper = styled.div.withConfig({
  displayName: "HomeWithShortcuts__GridWrapper"
})(["display:grid;grid-template-columns:repeat(12,1fr);gap:", ";padding:", ";"], getSpacing("2x"), getSpacing("4x", "6x"));
var IssueTopicsWrapper = styled.div.withConfig({
  displayName: "HomeWithShortcuts__IssueTopicsWrapper"
})(["grid-column:span 8;", "{grid-column:span 12;}"], responsiveMediaQueries.mobile);
var ShortcutsWrapper = styled.div.withConfig({
  displayName: "HomeWithShortcuts__ShortcutsWrapper"
})(["margin-top:-12px;grid-column:span 3;", "{grid-column:span 4;}"], responsiveMediaQueries.tablet);
var GridSpacer = styled.div.withConfig({
  displayName: "HomeWithShortcuts__GridSpacer"
})(["grid-column:span 1;", "{display:none;}"], responsiveMediaQueries.tablet);